import { assert, isArray, isArrayNotEmpty, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../domain'

import { type ConfigurationService, type DocumentationType, FileExtensionType } from '../../application'

import documentation from './documentation.json'

export type RawConfig = Readonly<{
    salesEmail: string
    supportEmail: string
    workbenchPath: URL['pathname']
    version: string
    httpsURL: URL
    wssURL: URL
    mqttPaths: readonly string[]
    pageSizes: readonly [short: number, medium: number, large: number, max: number]
    clientRegistryRefreshRate: number
    infoPageRefreshRate: number
    certificateFileExtension: string
    licenseFileExtension: string
    commissioningFile: Readonly<{ uploadExtensions: readonly string[], downloadMimeType: string }>
    interactionThrottle: number
    documentation: URL
    searchMinimum: number
    logoutWarningPeriod: number
    enableStorybook: boolean
}>

export class CybusConfigurationService implements ConfigurationService {
    private static readonly documentationLinks: ReadonlyRecord<DocumentationType, string> = documentation

    private readonly extensionsGetters = {
        [FileExtensionType.CERTIFICATE]: () => this.config.certificateFileExtension,
        [FileExtensionType.LICENSE]: () => this.config.licenseFileExtension,
        [FileExtensionType.SERVICE_COMMISSIONING]: () => this.config.commissioningFile.uploadExtensions,
    } as const

    static getDocumentationURL (type: DocumentationType, args: Pick<RawConfig, 'documentation' | 'version'>): URL {
        return new URL(`/${args.version.replaceAll('.', '-')}${this.documentationLinks[type]}`, args.documentation)
    }

    constructor (private readonly config: RawConfig) {}

    getResourcesPaginationSizes (): readonly [short: number, medium: number, large: number, max: number] {
        return this.config.pageSizes
    }

    getClientRegistryRefreshRate (): number {
        return this.config.clientRegistryRefreshRate
    }

    getApplicationMetadata (): ReadonlyRecord<string, string> {
        const entries = [
            'connect-src',
            "'self'",
            /**
             * HTTP and WSS urls are passed as is, so communication with them is whitelisted
             */
            this.config.httpsURL.href,
            this.config.wssURL.href,
            /**
             * Due to MQTT running on top of WSS, its paths need to be remapped and passed along too
             */
            ...this.config.mqttPaths.map((path) => new URL(path, this.config.wssURL).href),
        ]

        return { 'Content-Security-Policy': Array.from(new Set(entries)).join(' ') }
    }

    getDocumentationURL (type: DocumentationType): URL {
        return CybusConfigurationService.getDocumentationURL(type, this.config)
    }

    getWorkbenchURL (): URL {
        const { httpsURL, workbenchPath } = this.config
        return new URL(workbenchPath, httpsURL.origin)
    }

    getSupportedFileExtensions (type: FileExtensionType): ReadonlyNonEmptyArray<string> {
        const value = this.extensionsGetters[type]()
        const valueArray = isArray(value) ? value : [value]
        assert(isArrayNotEmpty(valueArray), new ConnectwareError(ConnectwareErrorType.CONFIGURATION_ERROR, 'File extensions are missconfigured', { type }))
        return valueArray
    }

    getServiceCommissioningFileDownloadMimeType (): string {
        return this.config.commissioningFile.downloadMimeType
    }

    getSalesEmail (): string {
        return this.config.salesEmail
    }

    getSupportEmail (): string {
        return this.config.supportEmail
    }

    getInfoPageRefreshRate (): number {
        return this.config.infoPageRefreshRate
    }

    getInteractionThrottle (): number {
        return this.config.interactionThrottle
    }

    getResourcesSearchThreshold (): number {
        return this.config.searchMinimum
    }

    getLogoutWarningPeriod (): number {
        return this.config.logoutWarningPeriod
    }

    isStorybookEnabled (): boolean {
        return this.config.enableStorybook
    }
}
